import {
    ExInput,
    ExMenuItem,
    ExSelect,
    ExToggle,
    MenuItemVariant,
    SelectType,
} from '@boomi/exosphere';
import type { IdentityProvider } from '../identity-providers/identityProviders';
import type { Option } from '../connectors/connectors';
import CopyField from '../shared/CopyField';
import ConnectorTools from './ConnectorTools';
import styles from '../shared/Form.module.css';

export interface FormValues {
    name: string;
    identityProviderId: string;
    clientId: string;
    enabled: boolean;
    userOptions: Record<string, string>;
}

export const EMPTY_FORM_VALUES: FormValues = {
    name: '',
    identityProviderId: '',
    clientId: '',
    enabled: true,
    userOptions: {},
};

interface ConnectionFormFieldsProps {
    values: FormValues;
    identityProviders: IdentityProvider[];
    isLoadingIdentityProviders?: boolean;
    onChange: (patch: Partial<FormValues>) => void;
    isEdit?: boolean;
    userAuthOptions?: Option[] | undefined;
    appAuthOptions?: Option[] | undefined;
    connectorId?: string | undefined;
    boomiSuppliesAppAuthOptions?: boolean;
    connectorName: string;
}

const ConnectionFormFields = ({
    values,
    identityProviders,
    isLoadingIdentityProviders = false,
    onChange,
    isEdit = false,
    userAuthOptions = [],
    appAuthOptions = [],
    boomiSuppliesAppAuthOptions,
    connectorId,
    connectorName,
}: ConnectionFormFieldsProps) => {
    const noIdentityProviders = !isLoadingIdentityProviders && identityProviders.length === 0;

    const idpPlaceholder = isLoadingIdentityProviders
        ? 'Loading…'
        : noIdentityProviders
          ? 'No identity providers available'
          : 'Select an identity provider…';

    return (
        <div className={styles.fields}>
            <ExInput
                label="Name"
                type="text"
                value={values.name}
                onInput={(e) => onChange({ name: (e.target as HTMLInputElement).value })}
                placeholder={`e.g. My ${connectorName} Config`}
                required
            />

            <div className={styles.field}>
                <ExSelect
                    label="Identity Provider"
                    valueBasedSelection
                    showSelectedLabel
                    required
                    placeholder={idpPlaceholder}
                    type={SelectType.SINGLE}
                    selected={values.identityProviderId}
                    disabled={isLoadingIdentityProviders || noIdentityProviders}
                    onChange={(e) =>
                        onChange({
                            identityProviderId: (e as CustomEvent<{ value: string }>).detail.value,
                        })
                    }
                >
                    {identityProviders.map((idp) => (
                        <ExMenuItem key={idp.id} value={idp.id} variant={MenuItemVariant.OPTION}>
                            {idp.name}
                        </ExMenuItem>
                    ))}
                </ExSelect>
            </div>

            {isEdit && connectorId && (
                <CopyField label="MCP URL" value={`${window.location.origin}/mcp/${connectorId}`} />
            )}

            {isEdit && values.clientId && <CopyField label="Client ID" value={values.clientId} />}

            {userAuthOptions
                .concat(!isEdit ? appAuthOptions : [])
                .filter((o) => o.isVisible)
                .map((option) => {
                    const optionFieldProps = {
                        label: option.label,
                        // When Boomi supplies app auth options, the user doesn't need to supply one.
                        required:
                            appAuthOptions.includes(option) && boomiSuppliesAppAuthOptions
                                ? false
                                : option.isRequired,
                        helpText: option.help ?? '',
                    };
                    return (
                        <div key={option.id} className={styles.field}>
                            {option.options ? (
                                <ExSelect
                                    {...optionFieldProps}
                                    valueBasedSelection
                                    showSelectedLabel
                                    type={SelectType.SINGLE}
                                    selected={values.userOptions[option.id] ?? ''}
                                    onChange={(e) =>
                                        onChange({
                                            userOptions: {
                                                ...values.userOptions,
                                                [option.id]: (e as CustomEvent<{ value: string }>)
                                                    .detail.value,
                                            },
                                        })
                                    }
                                >
                                    {Object.entries(option.options).map(([label, value]) => (
                                        <ExMenuItem
                                            key={value}
                                            value={value}
                                            variant={MenuItemVariant.OPTION}
                                        >
                                            {label}
                                        </ExMenuItem>
                                    ))}
                                </ExSelect>
                            ) : (
                                <ExInput
                                    {...optionFieldProps}
                                    type="text"
                                    value={values.userOptions[option.id] ?? ''}
                                    onInput={(e) =>
                                        onChange({
                                            userOptions: {
                                                ...values.userOptions,
                                                [option.id]: (e.target as HTMLInputElement).value,
                                            },
                                        })
                                    }
                                    {...(option.exampleValue
                                        ? { placeholder: option.exampleValue }
                                        : {})}
                                />
                            )}
                        </div>
                    );
                })}

            <ExToggle
                on={values.enabled}
                toggleLabel="Enabled"
                showToggleLabel
                onChange={(e) =>
                    onChange({ enabled: (e as unknown as CustomEvent<boolean>).detail })
                }
            />

            {connectorId && <ConnectorTools connectorId={connectorId} />}
        </div>
    );
};

export default ConnectionFormFields;
